import { Stack, useTheme } from "@mui/material";
import { type FC, useState } from "react";
import { EditAllocationDrawer } from "web_myplann/Goals/Components/EditAllocationDrawer";
import { GoalDetailSolutionList } from "web_myplann/Goals/Components/GoalDetailSolutionList";
import { GoalFutureContracts } from "web_myplann/Goals/Components/GoalFutureContracts";
import { PinContractChoiceDrawer } from "web_myplann/Goals/Components/PinContract/PinContractChoiceDrawer";
import { PinExistingContractDrawer } from "web_myplann/Goals/Components/PinContract/PinExistingContractDrawer";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { withLockGuard } from "web_myplann/Goals/Helpers/withLockGuard";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import type {
	GoalPortfolioListItemResponseDTO,
	GoalPortfolioResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AddRowButton } from "web_myplann/Shared/Components/AddRowButton";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";

export type PinStep = "closed" | "choose" | "existing" | "future";

type Props = {
	goal: GoalDetail;
	allocatedPortfolios: GoalPortfolioResponseDTO[];
	pinStep: PinStep;
	onPinStepChange: (next: PinStep) => void;
	onPinSelect: (picked: GoalPortfolioListItemResponseDTO) => void;
	onUnpin: (portfolioId: number) => void;
	onEditAllocation: (
		portfolioId: number,
		usagePercent: number,
		options?: { onSettled?: () => void },
	) => void;
	unpinningId: number | null;
	onLockedAction: (() => void) | null;
};

export const GoalDetailSolution: FC<Props> = ({
	goal,
	allocatedPortfolios,
	pinStep,
	onPinStepChange,
	onPinSelect,
	onUnpin,
	onEditAllocation,
	unpinningId,
	onLockedAction,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	const [editingAllocation, setEditingAllocation] =
		useState<GoalPortfolioResponseDTO | null>(null);

	const closePin = () => onPinStepChange("closed");
	const closeEdit = () => setEditingAllocation(null);

	const handleEditAllocation = withLockGuard(
		onLockedAction,
		(portfolio: GoalPortfolioResponseDTO) => setEditingAllocation(portfolio),
	);

	const futurePortfolios = goal.futurePortfolios;
	const hasFuture = futurePortfolios.length > 0;

	return (
		<CollapsibleSection
			title={t(Resources.Goals.Detail.Solution.title, {
				amount: formatAmount(
					Math.max(0, goal.savingsTarget - goal.coveredAmount),
				),
			})}
		>
			<Stack bgcolor={theme.palette.background.paper}>
				<GoalDetailSolutionList
					portfolios={allocatedPortfolios}
					unpinningId={unpinningId}
					hasFuture={hasFuture}
					dueYear={goal.dueYear}
					onUnpin={onUnpin}
					onEditAllocation={handleEditAllocation}
				/>
				<GoalFutureContracts
					goalId={Number(goal.id)}
					futurePortfolios={futurePortfolios}
					dueYear={goal.dueYear}
					createOpen={pinStep === "future"}
					onCloseCreate={closePin}
					onLockedAction={onLockedAction}
				/>
				<AddRowButton
					label={t(Resources.Goals.Detail.Solution.pinContract)}
					onClick={() => onPinStepChange("choose")}
				/>
			</Stack>
			<PinContractChoiceDrawer
				open={pinStep === "choose"}
				onClose={closePin}
				onPickExisting={() => onPinStepChange("existing")}
				onPickFuture={() => onPinStepChange("future")}
			/>
			<PinExistingContractDrawer
				open={pinStep === "existing"}
				onClose={closePin}
				onSelect={onPinSelect}
				onAddNew={() => onPinStepChange("future")}
				goalPortfolios={goal.portfolios}
			/>
			<EditAllocationDrawer
				open={editingAllocation !== null}
				portfolio={editingAllocation}
				onClose={closeEdit}
				onSubmit={onEditAllocation}
			/>
		</CollapsibleSection>
	);
};
